import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Notification} from '@models/header/notification';
import {
  NotificationModalComponent
} from '@components/header/notification/notification-modal/notification-modal.component';
import {HeaderNotificationService} from '@services/header/header-notification.service';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {ModalService} from '@services/modal/modal.service';

@Component({
    selector: 'scitis-notification',
    templateUrl: './notification.component.html',
    styleUrls: ['./notification.component.scss'],
    standalone: false
})
export class NotificationComponent implements OnInit, OnDestroy, OnChanges {

  @Input()
  isPageVisible: boolean = true;

  @Input()
  notificationRetention: number = 0;

  notifications: Notification[] = [];

  private CURRENT_PAGE_NAME = 'HeaderNotification';
  private interval: number | undefined;
  private NOTIFICATION_INTERVAL = 60 * 1000;
  private DEFAULT_NOTIFICATION_RETENTION_DAYS = 90;


  constructor(private headerNotificationService: HeaderNotificationService,
              private notificationService: NotificationService,
              private translateService: TranslateService,
              private modalService: ModalService,) {
  }

  get hasNotifications(): boolean {
    return this.notifications.length > 0;
  }

  get notificationsCount(): number {
    return this.notifications.length;
  }

  ngOnInit(): void {
    this.startLoadingNotifications();
  }

  ngOnDestroy() {
    this.clearInterval();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.isPageVisible && !this.isPageVisible) {
      this.clearInterval();
    }
    if (changes && changes.isPageVisible && this.isPageVisible) {
      this.startLoadingNotifications();
    }
  }

  openNotificationModal(notification: Notification) {
    const modalRef = this.modalService.open(NotificationModalComponent, {size: 'lg'});
    modalRef.componentInstance.notification = notification;
    modalRef.result.then((result) => {
      this.removeNotification(result);
    });
  }

  removeNotification(notification: Notification) {
    this.removeNotificationFromList(notification);
    this.headerNotificationService.removeNotification(notification).subscribe((result) => {
      console.log('Successfully removed notification ', result);
    }, (error) => {
      console.log('Could not update notification with id {}', notification.id, error);
      const errorMessage = this.translateService.instant('Could not update notification with id ') + notification.id;
      this.notificationService.error(errorMessage, error, this.CURRENT_PAGE_NAME, true);
    });
  }

  removeAllNotifications() {
    this.headerNotificationService.removeAllNotifications(this.notifications).subscribe(() => {
      this.notifications = [];
    }, (error) => {
      console.log('Could not update all notifications', error);
      const errorMessage = this.translateService.instant('Could not update all notifications');
      this.notificationService.error(errorMessage, error, this.CURRENT_PAGE_NAME, true);
    });
  }

  private removeNotificationFromList(notification: Notification) {
    const index = this.notifications.indexOf(notification);
    if (index >= 0) {
      this.notifications.splice(index, 1);
    }
  }

  private startLoadingNotifications() {
    this.clearInterval();
    this.getNotifications();
    this.interval = setInterval(() => {
      this.getNotifications();
    }, this.NOTIFICATION_INTERVAL);
  }

  private getNotifications() {
    this.headerNotificationService.getNotifications().subscribe((notifications: Notification[]) => {
      this.notifications = notifications;
      this.searchForExpiredNotifications();
    }, (error) => {
      console.log('Could not get notifications', error);
    });
  }

  private searchForExpiredNotifications() {
    for (const notification of this.notifications) {
      if (this.checkUsersRetention(notification.date)) {
        this.removeNotification(notification);
      }
    }
  }

  private clearInterval(): void {
    clearInterval(this.interval);
  }

  private checkUsersRetention(unixTimestamp: number): boolean {
    if (!this.notificationRetention) {
      this.notificationRetention = this.DEFAULT_NOTIFICATION_RETENTION_DAYS;
    }
    const currentDate = new Date();
    const notificationsDate = new Date(unixTimestamp);

    const daysBetween = Math.floor(
        (Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()) -
            Date.UTC(notificationsDate.getFullYear(), notificationsDate.getMonth(), notificationsDate.getDate()))
        / (1000 * 60 * 60 * 24));

    return daysBetween >= this.notificationRetention;
  }
}
